import UserGuideDownloadIcon from "../../../../assets/user-guide-download-white.svg";
import { NegotiationSelectedFilters } from "../../../../services/interfaces/IVendorQuoteAssessment";
import { FilterMultiPicker, FilterSinglePicker } from "./FilterPickers";
import {
    AllFiltersState,
    NavItem,
    QuoteSummaryMode,
    QuoteValue,
    VENDOR_QUOTE_ASSESSMENT_TABS,
    VENDOR_QUOTE_ASSESSMENT_TABS_WITH_ACCESS,
    VendorQuoteAssessmentTab,
    combineFirmFloat,
} from "../types";
import { useEffect } from "react";
import { sortCommodityFirst } from "../../../../utils/helpers/commodityOrder";

type VendorQuoteAssessmentHeaderProps = {
    width: number | string;
    navItem: NavItem;
    allFilters: AllFiltersState;
    filters: NegotiationSelectedFilters;
    district: string[] | null;
    vendor: string[] | null;
    quotes: QuoteValue;
    monthYear: string;
    contractType: string[] | null;
    benchmark: string;
    firstMonth: string;
    monthOfFilters: Array<{ month: string }>;
    skuToggle: boolean;
    skuQuoteDetailsToggle: QuoteSummaryMode;
    skuDistrict: string[];
    skuVendor: string[];
    skuQuoteDetailsDistrict: string[];
    skuQuoteDetailsVendor: string[];
    skuQuoteDetailsDate: string[];
    quoteSummaryContractType: string[] | null;
    quoteSummaryBenchmark: string;
    negotiationDateLabel: string;
    showBaseDistrictAndVendorFilters: boolean;
    showSkuCopperWireContractFilters: boolean;
    showQuoteSummaryCopperWireContractFilters: boolean;
    userDetails: any;
    onTabClick: (tabName: NavItem, tabLabel: string) => void;
    onCommodityChange: (value: string | null) => void;
    onBaseDistrictOpen: () => void;
    onBaseDistrictChange: (value: string[]) => void;
    onBaseDistrictClose: () => void;
    onBaseDistrictClean: () => void;
    onBaseVendorOpen: () => void;
    onBaseVendorChange: (value: string[]) => void;
    onBaseVendorClose: () => void;
    onBaseVendorClean: () => void;
    onQuoteSummaryDistrictChange: (value: string[]) => void;
    onQuoteSummaryDistrictClose: () => void;
    onQuoteSummaryDistrictClean: () => void;
    onQuoteSummarySupplierChange: (value: string[]) => void;
    onQuoteSummarySupplierClose: () => void;
    onQuoteSummarySupplierClean: () => void;
    onQuoteSummaryQuoteChange: (value: QuoteValue | null) => void;
    onQuoteSummaryDateChange: (value: string[]) => void;
    onQuoteSummaryDateClose: () => void;
    onQuoteSummaryDateClean: () => void;
    onSkuDistrictChange: (value: string[]) => void;
    onSkuDistrictClose: () => void;
    onSkuDistrictClean: () => void;
    onSkuVendorChange: (value: string[]) => void;
    onSkuVendorClose: () => void;
    onSkuVendorClean: () => void;
    onMonthYearChange: (value: string | null) => void;
    onDistrictComparisonQuoteTypeChange: (value: string | null) => void;
    onDistrictComparisonQuoteTypeClean: () => void;
    onSharedQuoteChange: (value: QuoteValue | null) => void;
    onBenchmarkChange: (value: string | null) => void;
    onBenchmarkClean: () => void;
    onContractTypeChange: (value: string | null) => void;
    onContractTypeClose: () => void;
    onContractTypeClean: () => void;
    onQuoteSummaryContractTypeChange: (value: string | null) => void;
    onQuoteSummaryContractTypeClose: () => void;
    onQuoteSummaryContractTypeClean: () => void;
    onQuoteSummaryBenchmarkChange: (value: string | null) => void;
    onQuoteSummaryBenchmarkClean: () => void;
    onOpenPurchasingConditions: () => void;
    onDownloadDistrictComparison: () => void;
    onApproveSkuClick?: () => void;
};

const actionButtonClassName =
    "absolute bottom-2 text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md cursor-pointer flex items-center";

const VendorQuoteAssessmentHeader = ({
    width,
    navItem,
    allFilters,
    filters,
    district,
    vendor,
    quotes,
    monthYear,
    contractType,
    benchmark,
    firstMonth,
    monthOfFilters,
    skuToggle,
    skuQuoteDetailsToggle,
    skuDistrict,
    skuVendor,
    skuQuoteDetailsDistrict,
    skuQuoteDetailsVendor,
    skuQuoteDetailsDate,
    quoteSummaryContractType,
    quoteSummaryBenchmark,
    negotiationDateLabel,
    showBaseDistrictAndVendorFilters,
    showSkuCopperWireContractFilters,
    showQuoteSummaryCopperWireContractFilters,
    userDetails,
    onTabClick,
    onCommodityChange,
    onBaseDistrictOpen,
    onBaseDistrictChange,
    onBaseDistrictClose,
    onBaseDistrictClean,
    onBaseVendorOpen,
    onBaseVendorChange,
    onBaseVendorClose,
    onBaseVendorClean,
    onQuoteSummaryDistrictChange,
    onQuoteSummaryDistrictClose,
    onQuoteSummaryDistrictClean,
    onQuoteSummarySupplierChange,
    onQuoteSummarySupplierClose,
    onQuoteSummarySupplierClean,
    onQuoteSummaryQuoteChange,
    onQuoteSummaryDateChange,
    onQuoteSummaryDateClose,
    onQuoteSummaryDateClean,
    onSkuDistrictChange,
    onSkuDistrictClose,
    onSkuDistrictClean,
    onSkuVendorChange,
    onSkuVendorClose,
    onSkuVendorClean,
    onMonthYearChange,
    onDistrictComparisonQuoteTypeChange,
    onDistrictComparisonQuoteTypeClean,
    onSharedQuoteChange,
    onBenchmarkChange,
    onBenchmarkClean,
    onContractTypeChange,
    onContractTypeClose,
    onContractTypeClean,
    onQuoteSummaryContractTypeChange,
    onQuoteSummaryContractTypeClose,
    onQuoteSummaryContractTypeClean,
    onQuoteSummaryBenchmarkChange,
    onQuoteSummaryBenchmarkClean,
    onOpenPurchasingConditions,
    onDownloadDistrictComparison,
    onApproveSkuClick,
}: VendorQuoteAssessmentHeaderProps) => {

    const isSkuMonthOf = Boolean(
        contractType && (contractType.includes("Firm") || contractType.includes("Float") || contractType.includes("Firm/Float")),
    );

    const isQuoteSummaryMonthOf = Boolean(
        quoteSummaryContractType &&
        (quoteSummaryContractType.includes("Firm") || quoteSummaryContractType.includes("Float") || quoteSummaryContractType.includes("Firm/Float")),
    );

    // Quote Details tab "Month of" filter always sources its options from the
    // API's `month_year` key (from getAllFiltersUpcomingMonthlyNegotiations)
    // instead of the `date` key.
    const quoteSummaryDateData = allFilters.month_year;

    // Ratio Analysis month filter always sources its options from the API's
    // `month_year` key (regardless of the Month of / Week of label).
    const skuDateData = allFilters.month_year;

    const isMonthOfForActiveTab =
        navItem === "quoteSummary" || navItem === "quoteDetails"
            ? isQuoteSummaryMonthOf || isSkuMonthOf
            : isSkuMonthOf;

    const hasQuoteDetailsAccess = Boolean(
        userDetails?.userModuleArray?.includes("Quote Details"),
    );

    const isDimUser = Boolean(
        userDetails?.role_name === "DIM"
    );

    const visibleTabs: VendorQuoteAssessmentTab[] = isDimUser
        ? [{
            key: "quoteSummary",
            label: "Quote Details",
            trackingLabel: "Quote Details",
        },]
        : hasQuoteDetailsAccess
            ? VENDOR_QUOTE_ASSESSMENT_TABS_WITH_ACCESS
            : VENDOR_QUOTE_ASSESSMENT_TABS;


    useEffect(() => {
        if (isDimUser) {
            onTabClick("quoteSummary", "Quote Details")
        }

    }, [isDimUser])


    return (
        <div
            className={`grow shrink-0 ${filters.commodity === "Copper Wire" && navItem !== "districtComparison"
                ? "basis-[28%]"
                : "basis-[20%]"
                } ps-[3%] pr-[5%] pt-[1%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative`}
        >
            <div className="flex justify-between items-center w-full">
                <div>
                    <div className="font-normal text-[2vw]">Supplier Quote Assessment</div>
                    <span className="font-bold text-fs16 text-[#858585]">
                        Negotiation {
                            filters.commodity !== "Copper Wire"
                                ? "Month"
                                : isMonthOfForActiveTab
                                    ? "Month"
                                    : "Week"
                        }
                        :&nbsp;
                        <span className="font-bold underline text-deloitteGreen">
                            {negotiationDateLabel}
                        </span>
                    </span>
                </div>

                <div className="flex flex-col gap-3">
                    {allFilters.commodity.length > 0 && (
                        <div
                            className={`flex justify-start items-center ${navItem !== "sKURatioAnalysis" ? "gap-8" : "gap-4"
                                }`}
                        >
                            <FilterSinglePicker
                                label="Commodity"
                                data={navItem === "renegotiateSummary" ? allFilters.commodity.filter((c: any) => c.commodity !== "Copper Wire") : sortCommodityFirst(allFilters.commodity)}
                                value={filters.commodity}
                                width={width}
                                labelKey="commodity"
                                valueKey="commodity"
                                onChange={onCommodityChange}
                                cleanable={false}
                                placeholder="All"
                            />

                            {showBaseDistrictAndVendorFilters && (
                                <FilterMultiPicker
                                    label="District"
                                    data={allFilters.districts}
                                    value={district ?? []}
                                    width={width}
                                    labelKey="district"
                                    valueKey="district"
                                    preventOverflow
                                    menuMaxHeight={150}
                                    onOpen={onBaseDistrictOpen}
                                    onChange={onBaseDistrictChange}
                                    onClose={onBaseDistrictClose}
                                    onClean={onBaseDistrictClean}
                                />
                            )}

                            {showBaseDistrictAndVendorFilters && (
                                <FilterMultiPicker
                                    label="Supplier"
                                    data={allFilters.vendor}
                                    value={vendor ?? []}
                                    width={width}
                                    labelKey="vendor"
                                    valueKey="vendor"
                                    preventOverflow
                                    menuMaxHeight={150}
                                    onOpen={onBaseVendorOpen}
                                    onChange={onBaseVendorChange}
                                    onClose={onBaseVendorClose}
                                    onClean={onBaseVendorClean}
                                />
                            )}

                            {navItem === "quoteSummary" &&
                                (skuQuoteDetailsToggle === "district" ? (
                                    <FilterMultiPicker
                                        label="District"
                                        data={allFilters.districts}
                                        value={skuQuoteDetailsDistrict}
                                        width={width}
                                        labelKey="district"
                                        valueKey="district"
                                        preventOverflow
                                        menuMaxHeight={150}
                                        onChange={onQuoteSummaryDistrictChange}
                                        onClose={onQuoteSummaryDistrictClose}
                                        onClean={onQuoteSummaryDistrictClean}
                                    />
                                ) : (
                                    <FilterSinglePicker
                                        label="District"
                                        data={allFilters.districts}
                                        value={skuQuoteDetailsDistrict[0] ?? null}
                                        width={width}
                                        labelKey="district"
                                        valueKey="district"
                                        onChange={(value) => {
                                            const nextValue = value ? [value] : [];
                                            onQuoteSummaryDistrictChange(nextValue);
                                        }}
                                        cleanable={false}
                                    />
                                ))}

                            {navItem === "quoteSummary" &&
                                (skuQuoteDetailsToggle === "supplier" ? (
                                    <FilterMultiPicker
                                        label="Supplier"
                                        data={allFilters.vendor}
                                        value={skuQuoteDetailsVendor}
                                        width={width}
                                        labelKey="vendor"
                                        valueKey="vendor"
                                        preventOverflow
                                        menuMaxHeight={150}
                                        onChange={onQuoteSummarySupplierChange}
                                        onClose={onQuoteSummarySupplierClose}
                                        onClean={onQuoteSummarySupplierClean}
                                    />
                                ) : (
                                    <FilterSinglePicker
                                        label="Supplier"
                                        data={allFilters.vendor}
                                        value={skuQuoteDetailsVendor[0] ?? null}
                                        width={width}
                                        labelKey="vendor"
                                        valueKey="vendor"
                                        onChange={(value) => {
                                            const nextValue = value ? [value] : [];
                                            onQuoteSummarySupplierChange(nextValue);
                                        }}
                                        cleanable={false}
                                    />
                                ))}

                            {navItem === "quoteSummary" && filters.commodity !== "Copper Wire" && (
                                <FilterSinglePicker
                                    label="Quote"
                                    data={allFilters.quotation_no}
                                    value={quotes}
                                    width={width}
                                    labelKey="quotation_name"
                                    valueKey="quotation_no"
                                    onChange={onQuoteSummaryQuoteChange}
                                    cleanable={false}
                                />
                            )}

                            {navItem === "quoteSummary" &&
                                (skuQuoteDetailsToggle === "month" ? (
                                    <FilterMultiPicker
                                        label={
                                            filters.commodity !== "Copper Wire"
                                                ? "Date"
                                                : isQuoteSummaryMonthOf
                                                    ? "Month of"
                                                    : "Week of"
                                        }
                                        data={quoteSummaryDateData}
                                        value={skuQuoteDetailsDate}
                                        width={width}
                                        labelKey="month"
                                        valueKey="month"
                                        onChange={onQuoteSummaryDateChange}
                                        onClose={onQuoteSummaryDateClose}
                                        onClean={onQuoteSummaryDateClean}
                                    />
                                ) : (
                                    <FilterSinglePicker
                                        label={
                                            filters.commodity !== "Copper Wire"
                                                ? "Date"
                                                : isQuoteSummaryMonthOf
                                                    ? "Month of"
                                                    : "Week of"
                                        }
                                        data={quoteSummaryDateData}
                                        value={skuQuoteDetailsDate[0] ?? quoteSummaryDateData?.[0]?.month ?? firstMonth ?? null}
                                        width={width}
                                        labelKey="month"
                                        valueKey="month"
                                        onChange={(value) => {
                                            if (!value) {
                                                return;
                                            }

                                            onQuoteSummaryDateChange([value]);
                                        }}
                                        cleanable={false}
                                    />
                                ))}

                            {navItem === "sKURatioAnalysis" &&
                                (!skuToggle ? (
                                    <FilterSinglePicker
                                        label="District"
                                        data={allFilters.districts}
                                        value={skuDistrict[0] ?? null}
                                        width={width}
                                        labelKey="district"
                                        valueKey="district"
                                        onChange={(value) => {
                                            const nextValue = value ? [value] : [];
                                            onSkuDistrictChange(nextValue);
                                        }}
                                        cleanable={false}
                                    />
                                ) : (
                                    <FilterMultiPicker
                                        label="District"
                                        data={allFilters.districts}
                                        value={skuDistrict}
                                        width={width}
                                        labelKey="district"
                                        valueKey="district"
                                        onChange={onSkuDistrictChange}
                                        onClose={onSkuDistrictClose}
                                        onClean={onSkuDistrictClean}
                                    />
                                ))}

                            {navItem === "sKURatioAnalysis" &&
                                (!skuToggle ? (
                                    <FilterMultiPicker
                                        label="Supplier"
                                        data={allFilters.vendor}
                                        value={skuVendor}
                                        width={width}
                                        labelKey="vendor"
                                        valueKey="vendor"
                                        onChange={onSkuVendorChange}
                                        onClose={onSkuVendorClose}
                                        onClean={onSkuVendorClean}
                                    />
                                ) : (
                                    <FilterSinglePicker
                                        label="Supplier"
                                        data={allFilters.vendor}
                                        value={skuVendor[0] ?? null}
                                        width={width}
                                        labelKey="vendor"
                                        valueKey="vendor"
                                        onChange={(value) => {
                                            const nextValue = value ? [value] : [];
                                            onSkuVendorChange(nextValue);
                                        }}
                                        cleanable={false}
                                    />
                                ))}

                            {navItem === "sKURatioAnalysis" && (
                                <FilterSinglePicker
                                    label={
                                        filters.commodity !== "Copper Wire"
                                            ? "Date"
                                            : isSkuMonthOf
                                                ? "Month of"
                                                : "Week of"
                                    }
                                    data={skuDateData}
                                    value={monthYear || skuDateData?.[0]?.month || firstMonth || null}
                                    width={width}
                                    labelKey="month"
                                    valueKey="month"
                                    onChange={onMonthYearChange}
                                    cleanable={false}
                                />
                            )}

                            {navItem === "districtComparison" && (
                                <FilterSinglePicker
                                    label={
                                        filters.commodity === "Copper Wire"
                                            ? isSkuMonthOf
                                                ? "Month"
                                                : "Week Start"
                                            : "Month"
                                    }
                                    data={allFilters.month_year}
                                    value={monthYear || null}
                                    width={width}
                                    labelKey="month"
                                    valueKey="month"
                                    onChange={onMonthYearChange}
                                    cleanable={false}
                                />
                            )}

                            {navItem === "districtComparison" &&
                                filters.commodity === "Copper Wire" && (
                                    <FilterSinglePicker
                                        label="Quote Type"
                                        data={allFilters.contract_type}
                                        value={
                                            contractType?.[0] ??
                                            allFilters.contract_type?.[0]?.contract_type ??
                                            null
                                        }
                                        width={width}
                                        labelKey="contract_type"
                                        valueKey="contract_type"
                                        className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]"
                                        preventOverflow
                                        menuMaxHeight={150}
                                        onChange={onDistrictComparisonQuoteTypeChange}
                                        onClean={onDistrictComparisonQuoteTypeClean}
                                        cleanable={false}
                                    />
                                )}

                            {(navItem === "sKURatioAnalysis" || navItem === "districtComparison") &&
                                filters.commodity !== "Copper Wire" && (
                                    <FilterSinglePicker
                                        label="Quote"
                                        data={allFilters.quotation_no}
                                        value={quotes}
                                        width={width}
                                        labelKey="quotation_name"
                                        valueKey="quotation_no"
                                        onChange={onSharedQuoteChange}
                                        cleanable={false}
                                    />
                                )}

                            {navItem === "districtComparison" &&
                                filters.commodity === "Copper Wire" && (
                                    <FilterSinglePicker
                                        label="Benchmark SKU"
                                        data={allFilters.benchmark}
                                        value={benchmark}
                                        width={width}
                                        labelKey="benchmark"
                                        valueKey="benchmark"
                                        className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]"
                                        onChange={onBenchmarkChange}
                                        onClean={onBenchmarkClean}
                                        cleanable={false}
                                    />
                                )}
                        </div>
                    )}

                    {showSkuCopperWireContractFilters && (
                        <div
                            className={`flex justify-start items-center ${navItem !== "sKURatioAnalysis" ? "gap-8" : "gap-4"
                                }`}
                        >
                            {navItem !== "renegotiateSummary" && (
                                <FilterSinglePicker
                                    label="Contract Type"
                                    data={combineFirmFloat(allFilters.contract_type)}
                                    value={contractType?.[0] ?? null}
                                    width={width}
                                    labelKey="contract_type"
                                    valueKey="contract_type"
                                    preventOverflow
                                    menuMaxHeight={150}
                                    cleanable={false}
                                    onChange={onContractTypeChange}
                                />
                            )}

                            <FilterSinglePicker
                                label="Base SKU"
                                data={allFilters.benchmark}
                                value={benchmark}
                                width={width}
                                labelKey="benchmark"
                                valueKey="benchmark"
                                className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]"
                                onChange={onBenchmarkChange}
                                onClean={onBenchmarkClean}
                                cleanable={false}
                            />
                        </div>
                    )}

                    {showQuoteSummaryCopperWireContractFilters && (
                        <div className="flex justify-start items-center gap-8">
                            <FilterSinglePicker
                                label="Contract Type"
                                data={combineFirmFloat(allFilters.contract_type)}
                                value={quoteSummaryContractType?.[0] ?? null}
                                width={width}
                                labelKey="contract_type"
                                valueKey="contract_type"
                                preventOverflow
                                menuMaxHeight={150}
                                cleanable={false}
                                onChange={onQuoteSummaryContractTypeChange}
                            />

                            <FilterSinglePicker
                                label="Base SKU"
                                data={allFilters.benchmark}
                                value={quoteSummaryBenchmark}
                                width={width}
                                labelKey="benchmark"
                                valueKey="benchmark"
                                className="flex flex-col justify-start items-start max-w-[max(180px,10vw)]"
                                onChange={onQuoteSummaryBenchmarkChange}
                                onClean={onQuoteSummaryBenchmarkClean}
                                cleanable={false}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-between items-end">
                <div className="absolute bottom-0 flex justify-start items-center gap-6 text-black font-normal text-[1.8vh]">
                    {visibleTabs.map((tab) => {
                        console.log(tab, navItem)
                        return (

                            <span
                                key={tab.key}
                                className={`${navItem === tab.key ? "font-bold border-b-2 border-black" : ""
                                    } cursor-pointer pb-[1vh]`}
                                onClick={() => onTabClick(tab.key, tab.trackingLabel)}
                            >
                                {tab.label}
                            </span>
                        )
                    })}
                </div>

                <div
                    onClick={onOpenPurchasingConditions}
                    className={`${navItem !== "quoteDetails" ? "hidden" : `${actionButtonClassName} right-[5.3vh]`
                        }`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="1vw"
                        height="1vw"
                        viewBox="0 0 12 16"
                        fill="none"
                    >
                        <path
                            d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                            fill="white"
                        />
                    </svg>
                    <span className="ml-[3px]">Purchasing Conditions</span>
                </div>

                <div
                    onClick={onDownloadDistrictComparison}
                    className={`${navItem !== "districtComparison"
                        ? "hidden"
                        : `${actionButtonClassName} right-[10vh]`
                        }`}
                >
                    <img
                        className="h-[2vh]"
                        src={UserGuideDownloadIcon}
                        alt="Download User Guide"
                    />
                    <button onClick={() => { }} className="text-[1.8vh] font-semibold text-white ml-[0.5vw]">
                        Download
                    </button>
                </div>

                <div
                    onClick={onApproveSkuClick}
                    className={`${navItem !== "sKURatioAnalysis" ? "hidden" : `${actionButtonClassName} right-[10vh]`
                        }`}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="1vw"
                        height="1vw"
                        viewBox="0 0 16 16"
                        fill="none"
                    >
                        <path
                            d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                            fill="white"
                        />
                    </svg>
                    <span className="ml-[3px]">Download</span>
                </div>
            </div>
        </div>
    )
}

export default VendorQuoteAssessmentHeader;
